import dayjs from 'dayjs';
import type React from 'react';
import { IS_NOT_SET_FILTER_LABEL, IS_SET_FILTER_LABEL } from './constants';
import type { FilterItem, FilterOption, FilterValue } from './types';
import { FilterDropdownType } from './types';
import { useFilterItems } from './useFilterItems';
import { createDeletedResourceLabel } from './useFilterLabelHelpers';
import { getValueAsArray } from './utils';

interface UseFilterLabelProps {
	filterOption: FilterOption;
	value: FilterValue;
}

export function useFilterLabel({
	filterOption,
	value,
}: UseFilterLabelProps): React.ReactNode {
	const config = filterOption.filterDropdownConfig;
	const isList =
		config.dropdownType === FilterDropdownType.List ||
		config.dropdownType === FilterDropdownType.NestedList;
	const isString = config.dropdownType === FilterDropdownType.String;
	const isDate = config.dropdownType === FilterDropdownType.Date;
	const isNumber = config.dropdownType === FilterDropdownType.Number;

	const {
		error: hasListItemsError,
		loading: isLoadingListItems,
		items: listItems,
	} = useFilterItems({
		getItems: isList ? config.getItems : undefined,
		getItemsById: isList ? config.getItemsById : undefined,
		value,
		filterOptionType: filterOption.type,
	});

	if (isDate) {
		const dateValue = value.value;
		const isRange = Array.isArray(dateValue) && dateValue.length === 2;

		if (
			((Array.isArray(dateValue) &&
				dateValue.some((v) => typeof v === 'string' && !dayjs(v).isValid())) ||
				(typeof dateValue === 'string' && !dayjs(dateValue).isValid())) &&
			config.dateValueLabelFormatter
		) {
			return config.dateValueLabelFormatter(dateValue);
		}

		return isRange
			? (dateValue as string[]).map((v) => dayjs(v).format('ll')).join(' - ')
			: dayjs(dateValue as string).format('ll');
	}

	const selectedValues: FilterItem[] = [];

	if (value.isNotSetApplied) {
		selectedValues.push(IS_NOT_SET_FILTER_LABEL);
	}

	if (value.isSetApplied) {
		selectedValues.push(IS_SET_FILTER_LABEL);
	}

	if (isList) {
		if (hasListItemsError) {
			return 'Error loading filter';
		}
		if (isLoadingListItems) {
			return 'Loading';
		}

		const values = getValueAsArray(value);

		selectedValues.push(
			...listItems.filter((item) => values.includes(item.value))
		);
	} else if (isString || isNumber) {
		// Users can select multiple values for string and number filters
		const values = getValueAsArray(value);
		for (const v of values) {
			if (v !== null && v !== '') {
				const label = config.valueLabelFormatter
					? config.valueLabelFormatter(v as string)
					: String(v);
				selectedValues.push({
					label,
					value: v as string,
				});
			}
		}
	}

	if (selectedValues.length === 0) {
		return null;
	}

	const hasDeletedItems = selectedValues.some(
		(item) => item.metadata?.isDeleted
	);

	const labelText =
		selectedValues.length > 1
			? `${selectedValues.length} ${config.multipleCountsSuffix ?? 'items'}`
			: selectedValues.map((item) => item.label).join(', ');

	if (hasDeletedItems) {
		return createDeletedResourceLabel(labelText, selectedValues.length === 1);
	}

	return labelText;
}
